/**
 * Reactive resource params (ADR-0022 Phase C · slice 2).
 *
 * A page resource may declare `deps` — the page-var names it reads. When a dep
 * changes, the runtime re-evaluates the resource's `{{ }}` parameter
 * expressions against the live scope (this module, `pageExpr.ts`) into CONCRETE
 * values and posts them to the resolve endpoint (`rt.refreshResource`). The
 * `{{ }}` engine stays ON THE CLIENT — the server is a pure data fetcher under
 * RLS (ADR-0022 Phase C slice 1). This module is pure + unit-tested.
 */
import { type Scope, renderTemplate } from "@/lib/pageExpr";
import type { PageQuery } from "@/lib/pageTypes";

/** Concrete runtime params posted to `POST .../resources/{name}/resolve`. */
export interface RuntimeParams {
  filters?: { column: string; op: string; value: unknown }[];
  sort?: { column: string; descending: boolean }[];
  page_size?: number;
  query?: Record<string, unknown>;
  /** http: the RENDERED url ({{ }} in path/query evaluated). Origin-locked
   *  server-side — the host can never be repointed. */
  url?: string;
  /** http: rendered header values (shallow-merged onto the spec headers). */
  headers?: Record<string, unknown>;
  /** http: the rendered request body (replaces the spec body). */
  body?: unknown;
}

/** A resource auto-refetches reactively only when it declares deps AND isn't
 *  `manual` (manual resources fetch on an explicit event, never on a dep tick). */
export function isReactive(spec: PageQuery): boolean {
  return (spec.deps?.length ?? 0) > 0 && spec.trigger !== "manual";
}

/** The page-var names a resource depends on, normalised (a leading `$page.` is
 *  tolerated so authors can write either `selectedRow` or `$page.selectedRow`). */
export function depNames(spec: PageQuery): string[] {
  return (spec.deps ?? []).map((d) => d.replace(/^\$page\./, ""));
}

/** True when EVERY dep value is empty (null / undefined / ""). A reactive
 *  resource is typically selection-driven (e.g. master-detail), so with nothing
 *  selected there's nothing to fetch — skipping avoids a query with an empty
 *  parameter (e.g. `id = null`). It still re-fires once a real value appears. */
export function allDepsEmpty(spec: PageQuery, vars: Record<string, unknown>): boolean {
  return depNames(spec).every((n) => {
    const v = vars[n];
    return v == null || v === "";
  });
}

/** Render one `{{ }}`-templated value against the scope (type-preserving for a
 *  single `{{ expr }}`); a non-template passes through, a bad binding → null. */
function evalValue(value: unknown, scope: Scope): unknown {
  if (typeof value !== "string" || !value.includes("{{")) return value;
  try {
    return renderTemplate(value, scope);
  } catch {
    return null;
  }
}

/** Deep-render every `{{ }}` string inside a value (for an http body, which is a
 *  JSON object/array whose leaf strings may be templated). */
function evalDeep(value: unknown, scope: Scope): unknown {
  if (typeof value === "string") return evalValue(value, scope);
  if (Array.isArray(value)) return value.map((v) => evalDeep(v, scope));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, evalDeep(v, scope)]),
    );
  }
  return value;
}

/**
 * Evaluate a resource's parameterised fields against the live scope into the
 * concrete `RuntimeParams` the resolve endpoint expects. Only the value side of
 * each filter and each `query` entry is templated; column / op / sort /
 * page_size are structural and pass through. Returns `{}` when the resource
 * carries no parameterised field (a plain refetch).
 */
export function computeRuntimeParams(spec: PageQuery, scope: Scope): RuntimeParams {
  const out: RuntimeParams = {};

  if (Array.isArray(spec.filters) && spec.filters.length) {
    out.filters = spec.filters.map((f) => ({
      column: String(f.column ?? ""),
      op: String(f.op ?? "eq"),
      value: evalValue(f.value, scope),
    }));
  }
  if (Array.isArray(spec.sort) && spec.sort.length) {
    out.sort = spec.sort.map((s) => ({
      column: String(s.column ?? ""),
      descending: Boolean(s.descending),
    }));
  }
  if (typeof spec.page_size === "number") out.page_size = spec.page_size;
  if (spec.query && typeof spec.query === "object") {
    out.query = Object.fromEntries(
      Object.entries(spec.query).map(([k, v]) => [k, evalValue(v, scope)]),
    );
  }
  // http: render the url path/query, each header value, and the body. The host
  // origin stays literal (validated at save) and is re-checked server-side, so
  // a templated path is filled but the fetch can't be repointed.
  if (typeof spec.url === "string" && spec.url.includes("{{")) {
    const rendered = evalValue(spec.url, scope);
    if (typeof rendered === "string") out.url = rendered;
  }
  if (spec.headers && typeof spec.headers === "object") {
    const entries = Object.entries(spec.headers as Record<string, unknown>);
    if (entries.length) {
      out.headers = Object.fromEntries(entries.map(([k, v]) => [k, evalValue(v, scope)]));
    }
  }
  if (spec.body !== undefined && spec.body !== null) {
    out.body = evalDeep(spec.body, scope);
  }
  return out;
}
